/*
 * Copyright (c) 2024 CalAmp Corp. All Rights Reserved
 */
import {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  getViewExcludeInheritedGroupState,
  setViewExcludeInheritedGroupState
} from '../utils/helpers';

export interface useExcludeInheritedGroupParams {
  name: string;
  action: Function;
  preference?: string;
};

/**
 * useExcludeInheritedGroup hook used to update the value for Exclude Inherited Group used by all the grids.
 *
 * ```typescript
 *
 * import {useExcludeInheritedGroup} from '@ion/applications/src/hooks/';
 *
 * const [viewExcludeInheritedGroup, toggleViewExcludeInheritedGroup] = useExcludeInheritedGroup();
 * ```
 */
export const useExcludeInheritedGroup = ({name, action, preference = ''}: useExcludeInheritedGroupParams) => {
  const dispatch               = useDispatch();
  const excludeInheritedGroups = useSelector(({[name]: {grid}}) => grid?.includeInheritedGroups) ||
  getViewExcludeInheritedGroupState(preference);
  const [
    viewExcludeInheritedGroup,
    toggleViewExcludeInheritedGroup
  ] = useState(excludeInheritedGroups);

  useEffect(() => {
    dispatch(action(viewExcludeInheritedGroup));
    if (preference && excludeInheritedGroups !== viewExcludeInheritedGroup) {
      setViewExcludeInheritedGroupState(preference, viewExcludeInheritedGroup);
    }
  }, [viewExcludeInheritedGroup]);

  return [
    viewExcludeInheritedGroup,
    toggleViewExcludeInheritedGroup
  ];
};

export default useExcludeInheritedGroup;
